import * as React from 'react'

import { CmsPage, getNestedObject, RenderCmsComponent } from 'bloomreach-experience-react-sdk'
import getConfig from 'next/config'

import { Product as SdProduct } from '@grandvision/analytics'
import { Products as ProductsApi } from '@grandvision/api-client'
import {
  CmsBreadcrumbs,
  DefaultPageProps,
  DynamicRoutes,
  getProductTypeUsps,
  HeadTags,
  OlapicContext,
} from '@grandvision/bloomreach'
import { CommerceToolsApi } from '@grandvision/commercetools'
import { MediaQuery } from '@grandvision/common'
import { withErrorPage } from '@grandvision/next'
import {
  Helpers,
  ProductDetail,
  ProductDetailProvider,
  ProductFrameMeasurements,
  SSP,
  VtoConfigProvider,
} from '@grandvision/products'
import { ReviewDisplay, ReviewsConfig, ReviewsProvider } from '@grandvision/reviews'
import { PageRoutes } from '@grandvision/routing'
import { CrossSellProducts } from '@grandvision/search'
import { getAttrValue, getLocalizedAttrValue } from '@grandvision/utils'

import componentDefinitions from '../../cms/component-definitions'
import Layout from '../../components/Layout'
import { getAlgoliaCrossSellConfig } from '../../util/algolia'
import {
  isContactLensEnabled,
  isContactLensStockEnabled,
  isCrossSellEnabled,
  isReviewsEnabled,
  isSolutionsEnabled,
  isVtoEnabled,
} from '../../util/feature-toggle'
import { getAllowedExtras } from '../../util/products'

type State = {
  currentBreakpoint?: MediaQuery.BreakpointName
  selectedGlasses: 'frameOnly' | 'frameAndMonofocal' | 'frameAndMultifocal'
  totalPrice?: number
}

type Props = {
  product: any
  sku: string
  locale: string
  productSEO: {
    title: string
    description: string
    imageUrl: string
    url: string
  }
  extrasData?: CommerceToolsApi.ProductProjection[]
  pageRoutes: PageRoutes
  productType: CommerceToolsApi.ProductTypeName
} & DefaultPageProps

const { publicRuntimeConfig } = getConfig()
const endpoints = publicRuntimeConfig.grandvision.endpoints || {}
const apiKey = publicRuntimeConfig.grandvision.apiKey || ''
const staticsUrl = publicRuntimeConfig.grandvision.staticsUrl || ''
const selfUrl = publicRuntimeConfig.selfUrl || ''
const vtoConfig = publicRuntimeConfig.ditto
const { apiKey: powerReviewsApiKey, groupId, opcoId } = publicRuntimeConfig.powerReviews

const reviewsConfig: Omit<ReviewsConfig, 'locale' | 'writeReviewPath'> = {
  apiKey: powerReviewsApiKey,
  groupId,
  opcoId,
}

class ProductDetailPage extends React.Component<Props, State> {
  state: State = {
    selectedGlasses: 'frameAndMonofocal',
  }

  static getInitialProps = async ({ asPath, query }) => {
    const { pageModel, request, isSolaris, locale, preview, pageRoutes } = query
    const { path } = request

    const [, slug, sku] = path.match(/.+\/(.+)\/([^\/\?]+)([\/\?].*)?$/)
    if (!sku || !slug) {
      throw new Error(
        `Can't extract slug and sku from ${path}, got sku: '${String(sku)}', slug: '${String(
          slug
        )}'`
      )
    }

    let product: CommerceToolsApi.ProductProjection | undefined
    try {
      product = await ProductsApi.getProductBySlug(endpoints.products, { slug }, apiKey)
    } catch (err) {
      // Non OK product service api response, return props to show the error page
      return {
        asPath,
        pageModel,
        statusCode: err.statusCode,
        buttonText: 'Voltar à página inicial',
        title: 'Algo parece estar quebrado...',
        subheader: 'Parece que você ficou um pouco perdido',
      }
    }

    const extrasId = Helpers.getExtrasId(product)
    let extrasData
    if (extrasId) {
      const { results } = await ProductsApi.query(endpoints.products, {
        filters: [`productType.id:"${extrasId}"`],
      })
      extrasData = results
    }

    // Used for SEO values
    const productSEO = {
      title: '',
      description: '',
      imageUrl: '',
      url: '',
    }

    let productType = ''

    if (
      product &&
      product.masterVariant &&
      product.masterVariant.attributes &&
      product.masterVariant.attributes.length > 0
    ) {
      const imageUrl = product.masterVariant.images && product.masterVariant.images[0].url
      productType = getNestedObject(pageModel, ['page', 'models', 'productType'])
      productSEO.title = Helpers.getProductTitle(
        productType,
        product,
        product.masterVariant,
        locale
      )
      const replacementFrequency = getLocalizedAttrValue(
        product.masterVariant,
        'replacementFrequency',
        locale
      )
      if (replacementFrequency) {
        productSEO.title = `${replacementFrequency} ${productSEO.title}`
      }

      productSEO.description = getLocalizedAttrValue(product.masterVariant, 'description', locale)
      productSEO.url = selfUrl + path
      productSEO.imageUrl = imageUrl
        ? staticsUrl + imageUrl + '?q=70&w=1200&h=630&fit=fill&fm=jpeg'
        : ''
    }

    return {
      asPath,
      request,
      pageModel,
      preview,
      product,
      extrasData,
      apiEndpoints: endpoints,
      sku,
      isSolaris,
      locale,
      productSEO,
      productType,
      pageRoutes,
    }
  }

  onSspChange = ({
    selectedGlasses,
    totalPrice,
  }: {
    selectedGlasses: 'frameOnly' | 'frameAndMonofocal' | 'frameAndMultifocal'
    totalPrice: number
  }) => {
    this.setState({ selectedGlasses, totalPrice })
  }

  onBreakpointChange = (breakpoint: MediaQuery.BreakpointName): void => {
    this.setState({ currentBreakpoint: breakpoint })
  }

  getSpecificationConfiguration = () => {
    const { productType } = this.props

    if (productType === 'Sunglasses') {
      return [
        {
          labelKey: 'products.specifications.brand',
          attribute: 'brand',
        },
        {
          labelKey: 'products.specifications.frameMaterial',
          attribute: 'frameMaterial',
          isLocalised: true,
        },
        {
          labelKey: 'products.sunglasses.features.polarized',
          attribute: 'polarized',
        },
        {
          labelKey: 'products.measurements.templeArm.length',
          attribute: 'templeLength',
        },
        {
          labelKey: 'gender',
          attribute: 'gender',
          isLocalised: true,
          isSet: true,
        },
        {
          labelKey: 'products.specifications.frameShape',
          attribute: 'frameShape',
          isLocalised: true,
        },
        {
          labelKey: 'products.specifications.faceShape',
          attribute: 'faceShape',
          isLocalised: true,
          isSet: true,
        },
      ]
    }
  }

  render() {
    const {
      pageModel,
      request,
      preview,
      asPath,
      isSolaris,
      product,
      extrasData,
      sku,
      labels = {},
      locale,
      pageRoutes,
      productSEO,
      productType,
    } = this.props

    if (!pageModel) {
      console.error(`No pageModel found for path: ${asPath}!`)
      return null
    }

    const routes =
      {
        ...pageRoutes,
        checkout: '/checkout',
      } || {}

    const featureToggles = {
      isSolutionsEnabled: isSolutionsEnabled(),
      isPrescriptionEnabled: isContactLensEnabled(),
      isContactLensStockEnabled: isContactLensStockEnabled(),
      isPrescriptionInfoEnabled: false,
      allowBackOrder: true,
    }

    const productTypeLabel = (labels?.products && labels.products[productType.toLowerCase()]) || ''
    const brandKey = String(getAttrValue(product.masterVariant, 'brandKey'))
    const writeReviewPath = pageModel?.page?.models?.routes?.['write-review']
    const reviewsEnabled = isReviewsEnabled() && !!product.reviewEnabled
    const productTypeUsps = getProductTypeUsps(pageModel)

    return (
      <React.Fragment>
        <HeadTags
          title={`${productTypeLabel} ${productSEO.title}`}
          description={productSEO.description}
          ogImage={productSEO.imageUrl}
          ogUrl={productSEO.url}
          noIndex={false}
          noFollow={false}
        />

        <CmsPage
          cmsUrls={publicRuntimeConfig.bloomreach.cmsUrls}
          pageModel={pageModel}
          request={request}
          componentDefinitions={componentDefinitions}
        >
          {() => (
            <Layout pageModel={pageModel} asPath={asPath} isSolaris={isSolaris} preview={preview}>
              <div className="product-detail-layout">
                <RenderCmsComponent path="main/top-container" />

                <DynamicRoutes pageModel={pageModel}>
                  {(routeFormatters) => (
                    <ProductDetailProvider
                      product={product}
                      sku={sku}
                      productType={productType}
                      locale={locale}
                      staticsUrl={staticsUrl}
                    >
                      <ReviewsProvider
                        enabled={reviewsEnabled}
                        config={{ ...reviewsConfig, locale, writeReviewPath }}
                      >
                        <CmsBreadcrumbs
                          pageModel={pageModel}
                          product={product}
                          productType={productType}
                          sku={sku}
                        />
                        {product && (
                          <VtoConfigProvider enabled={isVtoEnabled()} config={vtoConfig}>
                            <ProductDetail
                              routes={routes as any}
                              price={this.state.totalPrice}
                              productType={productType}
                              isPrescriptionEnabled={isContactLensEnabled()}
                              productTypeUsps={productTypeUsps}
                              {...featureToggles}
                              {...routeFormatters}
                            >
                              {CommerceToolsApi.isFrames(productType) && extrasData && (
                                <SSP
                                  product={product}
                                  initialGlasses="frameAndMonofocal"
                                  lensExtras={extrasData}
                                  lensExtrasResolver={getAllowedExtras}
                                  onChange={this.onSspChange}
                                  useIncludedFrame={false}
                                  useIncludedLensPack={true}
                                />
                              )}
                            </ProductDetail>
                          </VtoConfigProvider>
                        )}

                        {!CommerceToolsApi.isLenses(productType) && (
                          <ProductFrameMeasurements product={product} />
                        )}

                        <ReviewDisplay
                          productId={
                            CommerceToolsApi.isLenses(productType)
                              ? brandKey
                              : product.masterVariant.sku
                          }
                          variantId={CommerceToolsApi.isLenses(productType) ? undefined : sku}
                        />

                        {isCrossSellEnabled() && (
                          <CrossSellProducts
                            algoliaConfig={getAlgoliaCrossSellConfig(productType)}
                            formatProductUrl={routeFormatters.formatProductUrl}
                          />
                        )}

                        <SdProduct
                          product={product}
                          activeSKU={sku}
                          staticsUrl={staticsUrl}
                          url={productSEO.url}
                          locale={locale}
                        />
                      </ReviewsProvider>
                    </ProductDetailProvider>
                  )}
                </DynamicRoutes>

                <div className="product-detail-layout__components">
                  <OlapicContext.Provider value={sku}>
                    <RenderCmsComponent path="main/bottom-container" />
                  </OlapicContext.Provider>
                </div>
              </div>
            </Layout>
          )}
        </CmsPage>
      </React.Fragment>
    )
  }
}

export default withErrorPage(ProductDetailPage)
